import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CandlestickChart } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";

const inputCls = "bg-[#1E222D] border-[#2A2E39] text-[#EAECEF] placeholder:text-[#5E6673] focus-visible:ring-[#F0B90B]/50 font-mono";
const labelCls = "text-[10px] font-medium uppercase tracking-wider text-[#848E9C]";

export default function TradeEntryForm({ clients, onDone }) {
  const [symbol, setSymbol] = useState("XAUUSD");
  const [type, setType] = useState("buy");
  const [volume, setVolume] = useState("");
  const [openPrice, setOpenPrice] = useState("");
  const [closePrice, setClosePrice] = useState("");
  const [profit, setProfit] = useState("");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [openTime, setOpenTime] = useState("");
  const [closeTime, setCloseTime] = useState("");
  const [allocation, setAllocation] = useState("all");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [masterBalance, setMasterBalance] = useState("");
  const [masterAccountId, setMasterAccountId] = useState("");
  const [masterAccountName, setMasterAccountName] = useState("");

  const loadSettings = () =>
    api.get("/admin/settings").then((r) => {
      setMasterBalance(String(r.data.manual_balance ?? r.data.master_balance));
      setMasterAccountId(r.data.master_account_id || "");
      setMasterAccountName(r.data.master_account_name || "");
    }).catch(() => {});

  useEffect(() => { loadSettings(); }, []);

  const saveMaster = async () => {
    try {
      const payload = { master_account_id: masterAccountId || "" };
      if (!masterAccountId) {
        const val = parseFloat(masterBalance);
        if (!val || val <= 0) { toast.error("Enter a valid master balance"); return; }
        payload.master_balance = val;
      }
      const r = await api.patch("/admin/settings", payload);
      setMasterAccountName(r.data.master_account_name || "");
      toast.success(
        r.data.master_account_name
          ? `Master account set to ${r.data.master_account_name} (live balance: $${Number(r.data.master_balance).toLocaleString()})`
          : `Master balance set to $${Number(payload.master_balance).toLocaleString()}`
      );
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleClient = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!volume || !openPrice) {
      toast.error("Volume and open price are required");
      return;
    }
    if (allocation === "selected" && selected.length === 0) {
      toast.error("Select at least one client");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/admin/trades", {
        symbol,
        type,
        volume: parseFloat(volume),
        open_price: parseFloat(openPrice),
        close_price: closePrice ? parseFloat(closePrice) : null,
        sl: sl ? parseFloat(sl) : null,
        tp: tp ? parseFloat(tp) : null,
        profit: profit ? parseFloat(profit) : null,
        allocation,
        client_ids: allocation === "selected" ? selected : [],
        open_time: openTime || null,
        close_time: closeTime || null,
      });
      toast.success(
        res.data.status === "closed"
          ? `Trade #${res.data.ticket} executed & closed — P&L allocated`
          : `Position #${res.data.ticket} opened & allocated`
      );
      setVolume(""); setOpenPrice(""); setClosePrice(""); setProfit(""); setSl(""); setTp("");
      setOpenTime(""); setCloseTime("");
      setAllocation("all"); setSelected([]);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#2A2E39] bg-[#131722] p-4" data-testid="trade-entry-terminal">
      <div className="mb-4 flex items-center gap-2">
        <CandlestickChart className="h-4 w-4 text-[#F0B90B]" />
        <h2 className="font-heading text-base font-semibold text-[#EAECEF]">Trade Execution Terminal</h2>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-2 rounded-md border border-[#2A2E39] bg-[#1E222D] p-2.5">
          <Label className={labelCls}>Master Account (copy-trading source)</Label>
          <select
            data-testid="master-account-select"
            value={masterAccountId}
            onChange={(e) => setMasterAccountId(e.target.value)}
            className="w-full rounded-md border border-[#2A2E39] bg-[#0B0E14] px-3 py-2 text-sm text-[#EAECEF] focus:outline-none focus:ring-1 focus:ring-[#F0B90B]/50"
          >
            <option value="">Manual Balance</option>
            {masterAccountId && !clients.find((c) => c.id === masterAccountId) && (
              <option value={masterAccountId}>{masterAccountName || "Loading..."}</option>
            )}
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — #{c.account_no} ({money(c.balance)})
              </option>
            ))}
          </select>
          {!masterAccountId && (
            <Input data-testid="master-balance-input" type="number" step="0.01" min="0" value={masterBalance} onChange={(e) => setMasterBalance(e.target.value)} placeholder="1000" className={inputCls} />
          )}
          <button type="button" data-testid="master-balance-save-btn" onClick={saveMaster} className="w-full rounded-md border border-[#F0B90B]/40 bg-[#F0B90B]/10 px-3 py-2 text-xs font-semibold text-[#F0B90B] transition-colors hover:bg-[#F0B90B]/20">
            Save Master Settings
          </button>
          {masterAccountName && (
            <p className="text-[11px] text-[#0ECB81]" data-testid="active-master-label">Active master: {masterAccountName} — followers scale by balance ratio</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Symbol</Label>
          <select
            data-testid="trade-entry-symbol-select"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2 font-mono text-sm text-[#EAECEF] focus:outline-none focus:ring-1 focus:ring-[#F0B90B]/50"
          >
            <option value="XAUUSD">XAUUSD — Gold Spot</option>
            <option value="XAGUSD">XAGUSD — Silver Spot</option>
            <option value="GOLD">GOLD — Futures</option>
            <option value="BTCUSDT">BTCUSDT — Bitcoin</option>
            <option value="ETHUSDT">ETHUSDT — Ethereum</option>
            <option value="SOLUSDT">SOLUSDT — Solana</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="trade-entry-type-buy-btn"
            onClick={() => setType("buy")}
            className={`rounded-md py-2 text-sm font-bold tracking-wide transition-colors ${
              type === "buy" ? "bg-[#0ECB81] text-black" : "border border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
            }`}
          >
            BUY / LONG
          </button>
          <button
            type="button"
            data-testid="trade-entry-type-sell-btn"
            onClick={() => setType("sell")}
            className={`rounded-md py-2 text-sm font-bold tracking-wide transition-colors ${
              type === "sell" ? "bg-[#F6465D] text-white" : "border border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
            }`}
          >
            SELL / SHORT
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label className={labelCls}>Volume (Lots)</Label>
            <Input data-testid="trade-entry-volume-input" type="number" step="0.0001" min="0.0001" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="0.0001" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Open Price</Label>
            <Input data-testid="trade-entry-open-price-input" type="number" step="0.01" value={openPrice} onChange={(e) => setOpenPrice(e.target.value)} placeholder="2362.10" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Close Price (optional)</Label>
            <Input data-testid="trade-entry-close-price-input" type="number" step="0.01" value={closePrice} onChange={(e) => setClosePrice(e.target.value)} placeholder="Empty = open position" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>P&L Override (optional)</Label>
            <Input data-testid="trade-entry-pnl-input" type="number" step="0.01" value={profit} onChange={(e) => setProfit(e.target.value)} placeholder="Auto from prices" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Stop Loss</Label>
            <Input data-testid="trade-entry-sl-input" type="number" step="0.01" value={sl} onChange={(e) => setSl(e.target.value)} placeholder="—" className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Take Profit</Label>
            <Input data-testid="trade-entry-tp-input" type="number" step="0.01" value={tp} onChange={(e) => setTp(e.target.value)} placeholder="—" className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label className={labelCls}>Open Date & Time (optional)</Label>
            <Input data-testid="trade-entry-open-time-input" type="datetime-local" value={openTime} onChange={(e) => setOpenTime(e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Close Date & Time (optional)</Label>
            <Input data-testid="trade-entry-close-time-input" type="datetime-local" value={closeTime} onChange={(e) => setCloseTime(e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
          </div>
        </div>
        <p className="text-[11px] text-[#5E6673]">Leave date & time empty to use the current time automatically.</p>
        <div className="space-y-2">
          <Label className={labelCls}>P&L Allocation</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="trade-allocation-all-radio"
              onClick={() => setAllocation("all")}
              className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                allocation === "all" ? "border-[#F0B90B] bg-[#F0B90B]/10 text-[#F0B90B]" : "border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
              }`}
            >
              ALL CLIENTS
            </button>
            <button
              type="button"
              data-testid="trade-allocation-selected-radio"
              onClick={() => setAllocation("selected")}
              className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                allocation === "selected" ? "border-[#F0B90B] bg-[#F0B90B]/10 text-[#F0B90B]" : "border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
              }`}
            >
              SELECTED CLIENTS
            </button>
          </div>
          {allocation === "selected" && (
            <div className="max-h-36 space-y-1 overflow-y-auto rounded-md border border-[#2A2E39] bg-[#1E222D] p-2" data-testid="trade-allocation-client-list">
              {clients.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm transition-colors hover:bg-[#2A2E39]">
                  <span className="flex items-center gap-2 text-[#EAECEF]">
                    <input
                      type="checkbox"
                      data-testid={`trade-allocation-client-checkbox-${c.account_no}`}
                      checked={selected.includes(c.id)}
                      onChange={() => toggleClient(c.id)}
                      className="h-4 w-4 accent-[#F0B90B]"
                    />
                    {c.name}
                  </span>
                  <span className="font-mono text-xs text-[#848E9C]">{money(c.balance)}</span>
                </label>
              ))}
              {clients.length === 0 && <div className="px-2 py-3 text-center text-xs text-[#5E6673]">No clients yet</div>}
            </div>
          )}
          <p className="text-[11px] text-[#5E6673]">Copy-trading mode: each client's lots & P&L = master trade × (client balance ÷ master balance).</p>
        </div>
        <button
          data-testid="execute-trade-submit-btn"
          type="submit"
          disabled={loading}
          className={`w-full rounded-md py-2.5 text-sm font-bold tracking-wide transition-colors disabled:opacity-50 ${
            type === "buy" ? "bg-[#0ECB81] text-black hover:bg-[#10DC8C]" : "bg-[#F6465D] text-white hover:bg-[#FF536A]"
          }`}
        >
          {loading ? "Executing..." : `EXECUTE ${type.toUpperCase()} ${symbol}`}
        </button>
      </form>
    </div>
  );
}
